/**
 * variantMerge
 * ------------
 * Schema-agnostic merge/diff helpers behind every RHF-based bundle editor's
 * A/B variant system (originally built for VolumeDiscount's bundle-editor).
 *
 * `buildVariantValues` produces a complete "document" for a variant by
 * layering its stored overrides on top of the base document.
 * `buildOverrides` does the inverse: diffing an edited variant document
 * against base to produce the minimal overrides object to persist.
 *
 * `skipKeys` are top-level keys that always come from the base/primary
 * document and are never variant-specific (e.g. products, targeting).
 */

export type DeepPartial<T> = T extends object ? { [P in keyof T]?: DeepPartial<T[P]> } : T;

type AnyObj = Record<string, unknown>;

function isPlainObject(value: unknown): value is AnyObj {
    return (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        Object.prototype.toString.call(value) === "[object Object]"
    );
}

/**
 * Deep-merge two plain objects. Arrays in `override` always replace the same
 * key in `base` – they are never merged element-by-element.
 */
function deepMerge<T extends AnyObj>(base: T, override: DeepPartial<T>): T {
    const result: AnyObj = { ...base };

    for (const key of Object.keys(override) as (keyof T & string)[]) {
        const baseVal = base[key];
        const overrideVal = override[key as keyof DeepPartial<T>];

        if (overrideVal === undefined) {
            continue;
        }

        if (isPlainObject(baseVal) && isPlainObject(overrideVal)) {
            result[key] = deepMerge(baseVal as AnyObj, overrideVal as AnyObj);
        } else {
            result[key] = overrideVal;
        }
    }

    return result as T;
}

/**
 * Merge `base` and `overrides` into a complete variant document, then
 * re-enforce `skipKeys` from base (they must never be overridden).
 */
export function buildVariantValues<T extends object>(base: T, overrides: DeepPartial<T>, skipKeys: (keyof T)[]): T {
    const merged = deepMerge<AnyObj>(base as AnyObj, overrides as AnyObj) as T;

    for (const key of skipKeys) {
        merged[key] = base[key];
    }

    return merged;
}

/**
 * Recursively diff two plain objects.
 * Returns an object containing only keys where `edited` differs from `base`,
 * or `undefined` if the objects are identical.
 */
export function diffObjects(base: AnyObj, edited: AnyObj): AnyObj | undefined {
    const result: AnyObj = {};

    const allKeys = new Set([...Object.keys(base), ...Object.keys(edited)]);

    for (const key of allKeys) {
        const bVal = base[key];
        const eVal = edited[key];

        if (isPlainObject(bVal) && isPlainObject(eVal)) {
            const nested = diffObjects(bVal, eVal);
            if (nested !== undefined) {
                result[key] = nested;
            }
        } else if (Array.isArray(bVal) || Array.isArray(eVal)) {
            if (JSON.stringify(bVal) !== JSON.stringify(eVal)) {
                result[key] = eVal;
            }
        } else if (bVal !== eVal) {
            result[key] = eVal;
        }
    }

    return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * Build a minimal override object by diffing `base` against `editedVariant`,
 * with `skipKeys` always excluded (they must never appear in overrides).
 */
export function buildOverrides<T extends object>(base: T, editedVariant: T, skipKeys: (keyof T)[]): DeepPartial<T> {
    const rawDiff = diffObjects(base as AnyObj, editedVariant as AnyObj) ?? {};

    for (const key of skipKeys) {
        delete rawDiff[key as string];
    }

    return rawDiff as DeepPartial<T>;
}
